import { Component, DestroyRef, inject, input, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import {
  EsraPipingAPIClient,
  PipeClassViewModel,
  ReleaseObjectGroupRoleViewModel,
  UserType,
} from 'src/app/shared/models/autogenerated-piping';
import { CommentAuthorRole, CommentViewModel } from 'src/app/shared/models/comment.model';
import { CommentsService } from 'src/app/shared/services/comments.service';

/** Loaded comment data grouped by tab context. */
interface InfoPanelData {
  loading: boolean;
  loadError: boolean;
  revisionId: number | undefined;
  commentsByContext: {
    summary: CommentViewModel[];
    components: CommentViewModel[];
    valves: CommentViewModel[];
    branchtables: CommentViewModel[];
  };
  activeCommentTab: number;
}

@Component({
  selector: 'app-release-row-info-panel',
  templateUrl: './release-row-info-panel.component.html',
  styleUrls: ['./release-row-info-panel.component.scss'],
  standalone: false,
  // ViewEncapsulation.None is required so the Angular Material tab overrides
  // in the component stylesheet apply correctly without ::ng-deep.
  encapsulation: ViewEncapsulation.None,
})
export class ReleaseRowInfoPanelComponent implements OnInit {
  private readonly esraPipingAPIClient = inject(EsraPipingAPIClient);
  private readonly commentsService = inject(CommentsService);
  private readonly destroyRef = inject(DestroyRef);

  /** Pipe class id used to look up the active revision and load comments. */
  pipeClassId = input<string | undefined>();

  /** Display name of the release creator, shown at the top of the roles section. */
  creatorName = input<string | undefined>();

  /** Role assignments for this object group, used to populate the roles section. */
  objectGroupRoles = input<ReleaseObjectGroupRoleViewModel[] | undefined>();

  /** True when the current user is the release author or owner; controls visibility of the roles section. */
  canManageRelease = input<boolean>(false);

  /** Exposes `UserType` to the template for role-grouping bindings. */
  readonly UserType = UserType;

  /** Set of comment ids whose replies are currently expanded. */
  expandedReplies: Set<string> = new Set<string>();

  infoData: InfoPanelData = {
    loading: false,
    loadError: false,
    revisionId: undefined,
    commentsByContext: { summary: [], components: [], valves: [], branchtables: [] },
    activeCommentTab: 0,
  };

  ngOnInit(): void {
    // The component is created fresh each time the panel is opened (*ngIf in parent),
    // so loading in ngOnInit is sufficient — no change-tracking needed.
    this.loadComments();
  }

  /**
   * Returns the display names of all users in the object group who hold the given role.
   * @param userRole The role to filter by.
   * @returns An array of display names, empty when no members hold that role.
   */
  getFilteredRoles(userRole: UserType): string[] {
    return (this.objectGroupRoles() ?? [])
      .filter((r: ReleaseObjectGroupRoleViewModel) => r.userRole === userRole)
      .map((r: ReleaseObjectGroupRoleViewModel) => r.userDisplayName ?? 'Unknown');
  }

  /**
   * Maps a comment author's role to the human-readable label shown beside each comment.
   * Returns an empty string for null (legacy comments created before this field existed).
   * @param authorRole The role enum value from the comment.
   * @returns A human-readable role label, or an empty string.
   */
  getCommentAuthorRoleLabel(authorRole: CommentAuthorRole | null): string {
    switch (authorRole) {
      case CommentAuthorRole.Author: return 'author';
      case CommentAuthorRole.Approver: return 'release owner';
      case CommentAuthorRole.InternalReviewer: return 'internal reviewer';
      case CommentAuthorRole.ExternalReviewer: return 'contributor';
      default: return '';
    }
  }

  /**
   * Toggles the replies list open/closed for the given root comment id.
   * State survives tab switches within the same panel open/close cycle.
   */
  toggleReplies(commentId: string): void {
    if (this.expandedReplies.has(commentId)) {
      this.expandedReplies.delete(commentId);
    } else {
      this.expandedReplies.add(commentId);
    }
  }

  /** Returns true when the replies list for the given comment id is expanded. */
  isRepliesExpanded(commentId: string): boolean {
    return this.expandedReplies.has(commentId);
  }

  /**
   * Fetches the pipe class to resolve the active revision id, then loads all four
   * comment groups (summary, components, valves, branch tables) in parallel.
   */
  private loadComments(): void {
    const pipeClassId: string = this.pipeClassId() ?? '';
    if (!pipeClassId) {
      return;
    }

    this.infoData = {
      loading: true,
      loadError: false,
      revisionId: undefined,
      commentsByContext: { summary: [], components: [], valves: [], branchtables: [] },
      activeCommentTab: 0,
    };

    this.esraPipingAPIClient.getPipeClassByPipeClassId(pipeClassId)
      .pipe(
        first(),
        switchMap((pipeClass: PipeClassViewModel) => {
          const revisionNumericId: number | undefined = pipeClass.fromRevision?.id;
          if (revisionNumericId === undefined) {
            // No revision linked to this pipe class — leave comment sections empty.
            return of(null as null);
          }
          return this.commentsService.getAllComments(pipeClassId, revisionNumericId.toString()).pipe(
            map((comments) => ({
              revisionId: revisionNumericId,
              summary: comments.summary,
              components: comments.components,
              valves: comments.valves,
              branchtables: comments.branchtables,
            }))
          );
        }),
        catchError(() => {
          this.infoData.loading = false;
          this.infoData.loadError = true;
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.infoData.loading = false;
        if (result === null) { return; }
        this.infoData.revisionId = result.revisionId;
        this.infoData.commentsByContext = {
          summary: result.summary,
          components: result.components,
          valves: result.valves,
          branchtables: result.branchtables,
        };
      });
  }
}
